import { AxiosInstance } from 'axios'
import {
  Compliance,
  ComplianceHistory,
  ComplianceListOptions,
  ComplianceSubmissionsList,
  EntryIdCodes,
  Submission,
  UpdateComplianceParams,
  WaitlistEntry
} from 'types/compliance'
import { COMPLIANCE_ENDPOINTS, USER_ENDPOINTS } from 'api/ApiRoutes'
import { UserProfile } from 'types/user'
import { Team } from 'types/groups'
import { cleanObject, formatComplianceDateParams } from 'utils/Functions'
import { DriverFormPayload } from 'pages/dashboard/compliance/driver-form/types'
import { ErrorMessage, ID } from 'types/utility'
import { CheckVinResponse } from 'types/vehicles'

class ComplianceApi {
  constructor(private axiosInstance: AxiosInstance) {}

  getUserSubmission = async (args?: {
    annualized_mileage_year?: string
    use_new_endpoint: boolean
  }) => {
    const url = args?.use_new_endpoint
      ? COMPLIANCE_ENDPOINTS.COMPLIANCE_SUBMISSIONS
      : 'submissions/me'
    const d = await this.axiosInstance.get<Submission>(url, {
      params: args?.annualized_mileage_year
        ? {
            annualized_mileage_year: args.annualized_mileage_year
          }
        : undefined
    })
    return d.data
  }

  uploadComplianceImage = async ({
    inputName,
    file
  }: {
    inputName: string
    file: string | Blob
  }) => {
    const formData = new FormData()
    if (inputName === 'drivers_license_uploads') {
      formData.append('user[drivers_license_uploads][image]', file)
    }
    if (inputName === 'insurance_declaration_uploads') {
      formData.append('user[insurance_declaration_uploads][image]', file)
    }
    const d = await this.axiosInstance.put<UserProfile>(
      USER_ENDPOINTS.CURRENT_USER,
      formData,
      // Axios 1.x converts FormData to JSON when Content-Type is 'application/json' (set in defaultConfig).
      // Setting null removes that header so the browser sets multipart/form-data with the correct boundary.
      { headers: { 'Content-Type': 'multipart/form-data' } }
    )
    return d.data
  }

  /**
   * @deprecated since Diego's compliance refactor
   */
  DEPRECATED_getComplianceSubmissions = async ({
    team_id,
    page,
    page_size,
    filters
  }: {
    team_id: Team['token_id']
    filters?: ComplianceListOptions
    page?: number | string
    page_size?: number | string
  }) => {
    if (typeof team_id === 'undefined')
      Promise.reject(new Error('Invalid team id'))

    let reqFilters = {
      ...filters
    }

    if (filters?.review_date) {
      reqFilters = {
        ...reqFilters,
        ...formatComplianceDateParams(filters.review_date, 'review_date')
      }
    }

    if (filters?.submission_date) {
      reqFilters = {
        ...reqFilters,
        ...formatComplianceDateParams(
          filters.submission_date,
          'submission_date'
        )
      }
    }

    if (filters?.program_token_id === 'all_programs') {
      reqFilters = {
        ...reqFilters,
        program_token_id: undefined
      }
    }

    const url = 'teams/' + team_id + '/compliances'
    const d = await this.axiosInstance.get<ComplianceSubmissionsList>(url, {
      params: {
        ...cleanObject(reqFilters ?? {}),
        page,
        page_size
      }
    })
    return d.data
  }

  getComplianceSubmissions = async ({
    team_id,
    page,
    page_size,
    filters
  }: {
    team_id: Team['token_id']
    filters?: ComplianceListOptions
    page?: number | string
    page_size?: number | string
  }) => {
    if (typeof team_id === 'undefined')
      Promise.reject(new Error('Invalid team id'))

    let reqFilters = {
      ...filters
    }

    if (filters?.review_date) {
      reqFilters = {
        ...reqFilters,
        ...formatComplianceDateParams(filters.review_date, 'review_date')
      }
    }

    if (filters?.submission_date) {
      reqFilters = {
        ...reqFilters,
        ...formatComplianceDateParams(
          filters.submission_date,
          'submission_date'
        )
      }
    }

    if (filters?.program_token_id === 'all_programs') {
      reqFilters = {
        ...reqFilters,
        program_token_id: undefined
      }
    }

    const url = COMPLIANCE_ENDPOINTS.TEAM_COMPLIANCE_SUBMISSIONS(team_id)
    const d = await this.axiosInstance.get<ComplianceSubmissionsList>(url, {
      params: {
        ...cleanObject(reqFilters ?? {}),
        page,
        page_size
      }
    })
    return d.data
  }

  /**
   * @deprecated since Diego's compliance refactor
   */
  DEPRECATED_updateSubmission = async (data: DriverFormPayload) => {
    const submission_data = { submission: { ...data } }
    const d = await this.axiosInstance.put<Submission>(
      'submissions/me',
      submission_data
    )
    return d.data
  }

  updateSubmission = async (data: DriverFormPayload) => {
    const submission_data = { submission: { ...data } }
    const d = await this.axiosInstance.put<Submission>(
      COMPLIANCE_ENDPOINTS.COMPLIANCE_SUBMISSIONS,
      submission_data
    )
    return d.data
  }

  /**
   * @deprecated since Diego's compliance refactor
   */
  DEPRECATED_getComplianceData = async ({
    teamId,
    complianceId
  }: {
    teamId: Team['token_id']
    complianceId: UserProfile['token_id']
  }) => {
    const d = await this.axiosInstance.get<Compliance>(
      `teams/${teamId}/compliances/${complianceId}`
    )
    return d.data
  }

  getComplianceData = async ({
    teamId,
    complianceId
  }: {
    teamId: Team['token_id']
    complianceId: UserProfile['token_id']
  }) => {
    const d = await this.axiosInstance.get<Compliance>(
      COMPLIANCE_ENDPOINTS.TEAM_COMPLIANCE_SUBMISSION(teamId, complianceId)
    )
    return d.data
  }

  /**
   * @deprecated since Diego's compliance refactor
   */
  DEPRECATED_updateCompliance = async ({
    team_id,
    user_id,
    payload
  }: UpdateComplianceParams) => {
    const url = `teams/${team_id}/compliances/${user_id}`
    const compliance_data = { compliance: { ...payload } }
    const d = await this.axiosInstance.put(url, compliance_data)
    return d.data
  }

  updateCompliance = async ({
    team_id,
    user_id,
    payload
  }: UpdateComplianceParams) => {
    const url = COMPLIANCE_ENDPOINTS.TEAM_COMPLIANCE_SUBMISSION_REVIEW(
      team_id,
      user_id
    )
    const compliance_data = { compliance: { ...payload } }
    const d = await this.axiosInstance.put(url, compliance_data)
    return d.data
  }

  startComplianceReview = async ({
    team_id,
    user_id,
    payload
  }: UpdateComplianceParams) => {
    const url = COMPLIANCE_ENDPOINTS.TEAM_COMPLIANCE_SUBMISSION_START_REVIEW(
      team_id,
      user_id
    )
    const compliance_data = { compliance: { ...payload } }
    const d = await this.axiosInstance.put(url, compliance_data)
    return d.data
  }

  getWaitlistEntries = async () => {
    const d = await this.axiosInstance.get<WaitlistEntry[]>(
      COMPLIANCE_ENDPOINTS.COMPLIANCE_WAITLIST_ENTRIES
    )
    return d.data
  }

  createWaitlistEntry = async (entry_id_code: EntryIdCodes) => {
    const data = {
      waitlist_entry: {
        waitlist_id_code: entry_id_code
      }
    }
    const d = await this.axiosInstance.post<WaitlistEntry | ErrorMessage>(
      COMPLIANCE_ENDPOINTS.COMPLIANCE_WAITLIST_ENTRIES,
      data
    )
    return d.data
  }

  getUserComplianceHistory = async () => {
    const { data } = await this.axiosInstance.get<
      Record<'data', ComplianceHistory[]>
    >(USER_ENDPOINTS.COMPLIANCE_HISTORY)
    return data.data
  }

  getComplianceHistory = async ({ user_id }: { user_id: ID }) => {
    const { data } = await this.axiosInstance.get<
      Record<'data', ComplianceHistory[]>
    >(USER_ENDPOINTS.USER_COMPLIANCE_HISTORY(user_id))
    return data.data
  }

  checkVin = ({ vin }: { vin: string }) => {
    return this.axiosInstance
      .get<CheckVinResponse>(`${COMPLIANCE_ENDPOINTS.VIN_LOOKUP}?vin=${vin}`)
      .then((d) => {
        return d.data
      })
  }
}

export default ComplianceApi
